import { GoogleGenAI, Type } from "@google/genai";
import { ParsedTransactionData } from '../types';

interface ImageInput {
  inlineData: {
    data: string;
    mimeType: string;
  }
}

interface Part {
  text?: string;
  inlineData?: {
    data: string;
    mimeType: string;
  };
}

export const parseExpenseText = async (
  text: string, 
  images: { data: string, mimeType: string }[] = []
): Promise<ParsedTransactionData | null> => {
  if (!process.env.API_KEY) {
    console.error("API Key missing");
    return null;
  }

  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

  try {
    const parts: Part[] = [];

    // Construct the prompt first
    const prompt = `
      あなたは家計簿のアシスタントです。
      ${images.length > 0 ? '添付された画像（レシートや明細書）' : '提供されたテキスト'}から、取引情報を抽出してください。
      
      【重要】
      テキスト入力が空の場合や不明瞭な場合は、添付された画像の内容を最優先して解析してください。
      画像から日付、合計金額、店名、購入品目（商品リスト）などを読み取ってください。

      【抽出ルール】
      1. **種類 (type)**: 文脈から 'expense', 'income', 'charge', 'point' を判定してください。
         - expense: 通常の買い物・支払い。
         - income: 収入。
         - charge: チャージ・振替。
         - point: ポイントの「獲得(付与)」または「利用(支払い)」。
      2. **金額 (amount)**: 合計金額（数値のみ）。
      3. **場所・店名 (place)**: 店名など。ポイントの場合は "ポイント" としてください。
      4. **日付 (date)**: YYYY-MM-DD。不明なら今日。
      5. **カテゴリ (category)**: 
         - pointの場合: "ポイント"
         - その他: 適切なカテゴリ
      6. **購入品目 (items)**:
         - レシートまたはテキストに含まれる個別の商品名と価格をリストで抽出してください。
         - 割引などはマイナスの価格で表現しても構いません。
      7. **ポイント情報 (pointProvider / isPointUsage)**:
         - type='point'の場合のみ抽出。
         - pointProvider: [PayPayポイント, 楽天ポイント, dポイント, Pontaポイント, Vポイント, その他ポイント]
         - isPointUsage: 
            - true: ポイントを使った (支払い、減少)
            - false: ポイントをもらった (付与、獲得、増加)
      8. **決済方法 (method)**: 
         - レシートから支払方法（現金、クレジットカード、PayPay等）が読み取れる場合はそれを反映してください。
         - 不明な場合は "現金" と推測せず null または "その他" としてください。
         - chargeの場合: チャージ先。
         - pointの場合: "その他"。
      9. **詳細 (paymentDetail)**: 補足情報。
      10. **メモ (note)**: 購入品目などを要約。

      【入力】
      テキスト: "${text || ''}"
      画像枚数: ${images.length}枚
      
      JSON形式で返してください。
    `;

    // Add Prompt as the first text part
    parts.push({ text: prompt });

    // Add images as subsequent parts
    images.forEach(img => {
      parts.push({
        inlineData: {
          data: img.data,
          mimeType: img.mimeType
        }
      });
    });

    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: { parts },
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            type: { type: Type.STRING, enum: ['expense', 'income', 'charge', 'point'], description: "取引タイプ" },
            amount: { type: Type.NUMBER, description: "合計金額" },
            place: { type: Type.STRING, description: "場所・店名" },
            items: {
              type: Type.ARRAY,
              description: "購入商品リスト",
              items: {
                type: Type.OBJECT,
                properties: {
                  name: { type: Type.STRING, description: "商品名" },
                  amount: { type: Type.NUMBER, description: "商品の価格" }
                },
                required: ["name"]
              }
            },
            method: { type: Type.STRING, description: "決済方法" },
            sourceMethod: { type: Type.STRING, description: "チャージ元" },
            pointProvider: { type: Type.STRING, description: "ポイント種類(PayPayポイント等)" },
            isPointUsage: { type: Type.BOOLEAN, description: "ポイント利用フラグ(true=利用, false=獲得)" },
            paymentDetail: { type: Type.STRING, description: "詳細" },
            category: { type: Type.STRING, description: "カテゴリ" },
            date: { type: Type.STRING, description: "日付 YYYY-MM-DD" },
            time: { type: Type.STRING, description: "時刻 HH:mm" },
            note: { type: Type.STRING, description: "メモ" }
          },
          required: ["amount", "type"],
        }
      }
    });

    if (response.text) {
      const data = JSON.parse(response.text) as ParsedTransactionData;
      return data;
    }
    return null;
  } catch (error) {
    console.error("Gemini parse error:", error);
    return null;
  }
};

export const verifyStoreNameWithGoogleMaps = async (
  storeName: string,
  location?: { latitude: number; longitude: number }
): Promise<string | null> => {
  if (!process.env.API_KEY) return null;

  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    
    const prompt = `
      以下の店名が正しいかGoogle Mapsで確認し、正式名称を返してください。
      もし店名があいまいな場合は、最も可能性の高い正式名称を返してください。
      
      入力店名: "${storeName}"
      ${location ? `現在地: 緯度${location.latitude}, 経度${location.longitude} 周辺` : ''}

      返却フォーマット: 正式名称のみ（余計な説明は不要）
    `;

    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: prompt,
      config: {
        tools: [{ googleMaps: {} }],
        toolConfig: location ? {
          retrievalConfig: {
            latLng: {
              latitude: location.latitude,
              longitude: location.longitude
            }
          }
        } : undefined
      }
    });

    // Extract the store name from the grounding metadata or text
    // Usually the text response will contain the answer based on the grounding
    
    // Check grounding chunks first for direct place names
    const groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks;
    if (groundingChunks && groundingChunks.length > 0) {
      // Look for a map entry
      const mapChunk = groundingChunks.find(c => c.web?.title || (c as any).maps?.title); // Type definition might vary
      // The SDK type for groundingChunks might be generic, let's rely on text response which should be grounded
    }

    // Simple approach: The model should return the corrected name in text because we asked for it.
    // However, with googleMaps tool, the response might be conversational.
    // Let's trust the text output but trim it.
    
    return response.text?.trim() || null;

  } catch (error) {
    console.error("Google Maps verification error:", error);
    return null;
  }
};